/**
 * SmoothScrollProvider — weighted cinematic smooth scroll using Lenis.
 * Tuned to ease = 0.06 (heavier than Variant v1's 0.08). Bridges Lenis →
 * GSAP ScrollTrigger via the gsap ticker. Disabled under prefers-reduced-motion.
 */

import { useEffect, type ReactNode } from "react";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { smoothScroll } from "@/lib/timing";

gsap.registerPlugin(ScrollTrigger);

export function SmoothScrollProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReduced) return;

    const lenis = new Lenis({
      lerp: smoothScroll.lerp,
      smoothWheel: smoothScroll.smoothWheel,
      wheelMultiplier: smoothScroll.wheelMultiplier,
      touchMultiplier: smoothScroll.touchMultiplier,
    });

    lenis.on("scroll", ScrollTrigger.update);
    const tickerHandler = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(tickerHandler);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(tickerHandler);
      lenis.destroy();
    };
  }, []);

  return <>{children}</>;
}
